import { z } from 'zod';
// ============================================================
// Common Schemas (공통 스키마)
// ============================================================
export const ApiSuccessResponseSchema = (dataSchema) => z.object({
    success: z.literal(true),
    data: dataSchema,
    message: z.string().optional(),
});
export const ApiErrorResponseSchema = z.object({
    success: z.literal(false),
    error: z.object({
        code: z.string(),
        message: z.string(),
        details: z.record(z.string(), z.any()).optional(),
    }),
});
export const PaginationQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(20),
    sortBy: z.string().optional(),
    sortOrder: z.enum(['asc', 'desc']).default('desc'),
});
export const PaginationMetaSchema = z.object({
    page: z.number().int().min(1),
    pageSize: z.number().int().min(1),
    total: z.number().int().min(0),
    totalPages: z.number().int().min(0),
    hasNext: z.boolean(),
    hasPrev: z.boolean(),
});
export const IdParamSchema = z.object({
    id: z.string().cuid2(),
});
export const DateRangeQuerySchema = z.object({
    startDate: z.coerce.date().optional(),
    endDate: z.coerce.date().optional(),
});
export const SearchQuerySchema = z.object({
    q: z.string().min(1).max(100).optional(),
    filter: z.record(z.string(), z.string()).optional(),
});
// ============================================================
// File Upload Schemas
// ============================================================
export const FileUploadResponseSchema = z.object({
    url: z.string().url(),
    filename: z.string(),
    size: z.number().int().min(0),
    mimeType: z.string(),
    uploadedAt: z.date(),
});
// ============================================================
// Validation Helpers
// ============================================================
export const isCuid2 = (value) => {
    return z.string().cuid2().safeParse(value).success;
};
export const isValidUrl = (value) => {
    return z.string().url().safeParse(value).success;
};
export const isValidDateRange = (startDate, endDate) => {
    return startDate <= endDate;
};
export const createSuccessResponse = (data, message) => ({
    success: true,
    data,
    ...(message && { message }),
});
export const createErrorResponse = (code, message, details) => ({
    success: false,
    error: {
        code,
        message,
        ...(details && { details }),
    },
});
// ============================================================
// Error Codes
// ============================================================
export const ERROR_CODES = {
    // Auth
    UNAUTHORIZED: 'UNAUTHORIZED',
    FORBIDDEN: 'FORBIDDEN',
    INVALID_TOKEN: 'INVALID_TOKEN',
    TOKEN_EXPIRED: 'TOKEN_EXPIRED',
    AUTH_FAILED: 'AUTH_FAILED',
    ACCOUNT_DELETED: 'ACCOUNT_DELETED',
    INVALID_STATE: 'INVALID_STATE',
    // Validation
    VALIDATION_ERROR: 'VALIDATION_ERROR',
    INVALID_INPUT: 'INVALID_INPUT',
    INVALID_REQUEST: 'INVALID_REQUEST',
    LIMIT_EXCEEDED: 'LIMIT_EXCEEDED',
    // Resources
    NOT_FOUND: 'NOT_FOUND',
    ALREADY_EXISTS: 'ALREADY_EXISTS',
    USER_NOT_FOUND: 'USER_NOT_FOUND',
    // Payment
    PAYMENT_FAILED: 'PAYMENT_FAILED',
    INSUFFICIENT_CREDITS: 'INSUFFICIENT_CREDITS',
    ALREADY_REFUNDED: 'ALREADY_REFUNDED',
    INVALID_STATUS: 'INVALID_STATUS',
    REFUND_PERIOD_EXPIRED: 'REFUND_PERIOD_EXPIRED',
    ACTIVATION_NOT_REFUNDABLE: 'ACTIVATION_NOT_REFUNDABLE',
    CREDITS_USED: 'CREDITS_USED',
    NO_PAYMENT_KEY: 'NO_PAYMENT_KEY',
    TOSS_STATUS_MISMATCH: 'TOSS_STATUS_MISMATCH',
    // AI
    AI_GENERATION_FAILED: 'AI_GENERATION_FAILED',
    MODEL_DISABLED: 'MODEL_DISABLED',
    MISSING_PROMPT: 'MISSING_PROMPT',
    PROMPT_BUILD_FAILED: 'PROMPT_BUILD_FAILED',
    INVALID_SCENES: 'INVALID_SCENES',
    INVALID_REF_PHOTOS: 'INVALID_REF_PHOTOS',
    ALBUM_NOT_FOUND: 'ALBUM_NOT_FOUND',
    THEME_NOT_FOUND: 'THEME_NOT_FOUND',
    // Upload/File
    UPLOAD_FAILED: 'UPLOAD_FAILED',
    INVALID_FILE: 'INVALID_FILE',
    INVALID_TYPE: 'INVALID_TYPE',
    FILE_TOO_LARGE: 'FILE_TOO_LARGE',
    PRESIGN_FAILED: 'PRESIGN_FAILED',
    // Admin
    STYLESHEET_IN_USE: 'STYLESHEET_IN_USE',
    TEMPLATE_IN_USE: 'TEMPLATE_IN_USE',
    IN_USE: 'IN_USE',
    INVALID_DESIGN: 'INVALID_DESIGN',
    DUPLICATE_SLUG: 'DUPLICATE_SLUG',
    FK_DEPENDENCY: 'FK_DEPENDENCY',
    // Rate Limiting
    RATE_LIMIT_EXCEEDED: 'RATE_LIMIT_EXCEEDED',
    // Invitation
    PREMIUM_REQUIRED: 'PREMIUM_REQUIRED',
    INVITATION_EXPIRED: 'INVITATION_EXPIRED',
    EXTENSION_ALREADY_USED: 'EXTENSION_ALREADY_USED',
    INVALID_PASSWORD: 'INVALID_PASSWORD',
    SLOT_ALREADY_USED: 'SLOT_ALREADY_USED',
    MAX_INVITATIONS_REACHED: 'MAX_INVITATIONS_REACHED',
    // General
    INTERNAL_ERROR: 'INTERNAL_ERROR',
    SERVICE_UNAVAILABLE: 'SERVICE_UNAVAILABLE',
    SEARCH_ERROR: 'SEARCH_ERROR',
    DOWNLOAD_ERROR: 'DOWNLOAD_ERROR',
};
//# sourceMappingURL=common.js.map